/**
 * Profil (owner's employee UI brief, 2026-09-22, screen 8): the low-frequency personal things,
 * moved off Min side. Contact and employment are read only because only a manager may change
 * them, and the page says so instead of drawing edit controls; the language is the person's own
 * and is changed here. Documents and the work-time history (AML §10-7, ADR 0021) come with it.
 * Notification and privacy settings are not offered: the web has none (parked).
 */
import { useMe, useTimeBank, useWorkTimeChanges } from '@/api/data';
import { useAuth } from '@/auth/store';
import { LOCALES, T, useI18n } from '@/i18n';
import { MineDokumenter } from '@/pages/MineDokumenter';
import { TimeBankView } from '@/pages/ansatt/TimeBank';
import { useTenant } from '@/shell/tenant';
import { Chip, LoadError, Skeleton } from '@/ui';
import { fmtPhone, fullName, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import History from '../arbeidstid/History';
import { PassordCard } from './Passord';
import { Avatar, Card } from './parts';

export default function Profil() {
  const user = useAuth((s) => s.user);
  const { tenant } = useTenant();
  const { locale, setLocale } = useI18n();
  const meQ = useMe(user?.id);
  const histQ = useWorkTimeChanges({ mine: true });
  // the person's own overtime bank, only when they have an avspasering agreement (plan 0027 G2)
  const bank = useTimeBank('mine').data;
  const me = meQ.data;
  const rows = histQ.data ?? [];
  const title = (me as { title?: string | null } | undefined)?.title;

  return (
    <>
      <div className="ph">
        <div>
          <h1>{T('Profil')}</h1>
          <div className="sub">{T('Personopplysninger og innstillinger')}</div>
        </div>
      </div>
      <section className="card2 ecard" data-testid="profile-head">
        <div className="cb eprof big">
          <Avatar u={me ?? user} lg />
          <span className="ebody">
            <b>{me ? fullName(me) : user ? fullName(user) : ''}</b>
            <span>
              <Icon name="pin" /> {tenant?.name ?? ''}
            </span>
            {me?.vacation_days_balance != null && (
              <Chip tone="ok" small>
                {T('{n} feriedager igjen', { n: me.vacation_days_balance })}
              </Chip>
            )}
          </span>
        </div>
      </section>
      <div className="egrid2">
        <Card title={T('Kontaktinformasjon')} sub={T('endres av lederen')} testid="contact">
          {me ? (
            <>
              <div className="prow">
                <b>{T('Telefon')}</b>
                <span className="pv">{fmtPhone(me.phone) || '–'}</span>
              </div>
              <div className="prow">
                <b>{T('E-post')}</b>
                <span className="pv">{me.email || '–'}</span>
              </div>
              <p className="hint">{T('Er noe feil, be lederen rette det. Du kan ikke endre det selv.')}</p>
            </>
          ) : meQ.isError ? (
            // a failed fetch is not «Henter…» for good (#272 B-33)
            <LoadError what={T('profilen din')} onRetry={() => meQ.refetch()} />
          ) : (
            <Skeleton />
          )}
        </Card>
        <Card title={T('Arbeidsforhold')} testid="employment">
          {me ? (
            <>
              <div className="prow">
                <b>{T('Team')}</b>
                <span className="pv">{(me.teams ?? []).map((t) => t.name).join(', ') || '–'}</span>
              </div>
              {title ? (
                <div className="prow">
                  <b>{T('Stilling')}</b>
                  <span className="pv">{title}</span>
                </div>
              ) : null}
              <div className="prow">
                <b>{T('Avtalt tid')}</b>
                <span className="pv">
                  {me.max_hours_per_week == null
                    ? T('37,5* t per uke')
                    : T('{h} t per uke', { h: num(me.max_hours_per_week) })}
                </span>
              </div>
              <div className="prow">
                <b>{T('Feriedager')}</b>
                <span className="pv">
                  {T('{left} igjen av {total}', {
                    left: me.vacation_days_balance ?? '–',
                    total: me.total_vacation_days_per_year ?? '–',
                  })}
                </span>
              </div>
            </>
          ) : meQ.isError ? (
            <p className="hint">–</p>
          ) : (
            <Skeleton />
          )}
        </Card>
        <Card title={T('Språk')} testid="language">
          <div className="fld">
            <span className="lbl">{T('Språk i systemet')}</span>
            <select
              className="inp w"
              aria-label={T('Språk')}
              value={locale}
              onChange={(e) => setLocale(e.target.value as typeof locale)}
            >
              {LOCALES.map(([k, n]) => (
                <option key={k} value={k}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </Card>
        <PassordCard />
        <Card
          title={T('Endringer i timene mine')}
          sub={T('stemplinger, rettelser og godkjenninger')}
          testid="history"
        >
          {histQ.isLoading ? (
            <Skeleton />
          ) : (
            <details>
              <summary>
                {rows.length} {rows.length === 1 ? T('endring') : T('endringer')}
              </summary>
              <History rows={rows.slice(0, 50)} empty={T('Ingen endringer ennå.')} />
            </details>
          )}
        </Card>
        {(bank?.agreed_on || bank?.flex) && (
          <Card title={T('Timebank')} sub={T('overtid til avspasering')} testid="my-timebank">
            <TimeBankView bank={bank} />
          </Card>
        )}
      </div>
      <MineDokumenter />
    </>
  );
}
